import { computed, Injectable, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { createStore, select, withProps } from '@ngneat/elf';
import { Observable } from 'rxjs';

import { TAppAsset, TAppStyle, AppConfig } from '../shared/schemas/settings';
import { config } from '../../environments/environment';
import { configDefault } from '../shared/constants/app-config-default';

declare global {
  // eslint-disable-next-line no-var
  var andorAppConfigStore: AppConfigStore | undefined;
}

const APP_SETTINGS_STORE_NAME = 'andorAppSettings.v1';

const appConfigStore = createStore(
  { name: APP_SETTINGS_STORE_NAME },
  withProps<AppConfig>(configDefault)
);

/**
 * App configuration (title, gateway, colours, logo/background assets).
 *
 * The elf store value is the **draft** the UI edits live (so theme changes
 * apply immediately); `saved` is the last state confirmed by the server, and
 * `pendingFiles` holds image files selected but not yet uploaded. Keeping all
 * three here means "is anything unsaved?", "discard my changes" and "the server
 * accepted this" are single operations that cannot drift apart.
 */
@Injectable({
  providedIn: 'root',
})
export class AppConfigStore {
  store = appConfigStore;
  appSettings$ = appConfigStore.pipe(select((state) => state));
  appAssets$ = appConfigStore.pipe(select((state) => state.assets));
  appStyles$ = appConfigStore.pipe(select((state) => state.styles));
  appTitle$ = appConfigStore.pipe(select((state) => state.title));
  appColorMode$ = appConfigStore.pipe(select((state) => state.colorMode));
  // Backend-driven flag — see `developerMode` on `appConfigSchema`.
  developerMode$ = appConfigStore.pipe(select((state) => state.developerMode));

  readonly #current = toSignal(appConfigStore, { initialValue: appConfigStore.value });
  readonly #saved = signal<AppConfig>(configDefault);
  readonly #pendingFiles = signal<ReadonlyMap<string, File>>(new Map());

  /** Last configuration confirmed by the server (what Cancel reverts to). */
  readonly saved = this.#saved.asReadonly();
  /** Image files selected in the UI but not uploaded yet, keyed by asset key. */
  readonly pendingFiles = this.#pendingFiles.asReadonly();
  /** Server-owned file sharing limits (only the admin API changes them). */
  readonly fileSharing = computed(() => this.#saved().fileSharing);
  /** True while the draft differs from `saved`. */
  readonly hasConfigChanged = computed(() => !isAppConfigEqual(this.#current(), this.#saved()));

  constructor() {
    if (config.exposeDevTools) {
      globalThis.andorAppConfigStore = this;
    }
  }

  get value(): AppConfig {
    return this.store.value;
  }

  get value$(): Observable<AppConfig> {
    return this.store.asObservable();
  }

  getAsset$(id: string): Observable<TAppAsset | undefined> {
    return this.store.pipe(select((state) => state.assets[id]));
  }

  getStyle$(id: string): Observable<TAppStyle | undefined> {
    return this.store.pipe(select((state) => state.styles.find((style) => style.id === id)));
  }

  getAsset(id: string): TAppAsset | undefined {
    return this.store.value.assets[id];
  }

  getStyle(id: string): TAppStyle | undefined {
    return this.store.value.styles.find((style) => style.id === id);
  }

  update(appSettings: Partial<AppConfig>) {
    this.store.update((state) => ({
      ...state,
      ...appSettings,
    }));
  }

  updateAsset(id: string, asset: TAppAsset) {
    this.store.update((state) => ({
      ...state,
      assets: {
        ...state.assets,
        [id]: {
          ...state.assets[id],
          ...asset,
        },
      },
    }));
  }

  updateStyle(style: Partial<TAppStyle> & { id: string }) {
    this.store.update((state) => {
      return {
        ...state,
        styles: state.styles.map((s) => (s.id === style.id ? { ...s, ...style } : s)),
      };
    });
  }

  /** Choose a bundled asset and discard any upload superseded by that choice. */
  replaceAsset(id: string, asset: TAppAsset) {
    this.#pendingFiles.update((files) => {
      const next = new Map(files);
      next.delete(id);
      return next;
    });
    this.updateAsset(id, asset);
  }

  /**
   * Applies configuration confirmed by the server (startup, realtime update,
   * successful save) to both the draft and the saved baseline. Pending files
   * are dropped: either they were just uploaded, or the server state
   * superseded them.
   */
  commit(appSettings: Partial<AppConfig>) {
    this.update(appSettings);
    this.#saved.set(this.store.value);
    this.#pendingFiles.set(new Map());
  }

  /**
   * Records server state as the new baseline for realtime updates that arrive
   * while the user is editing. Cancel then lands on the newest server state,
   * and draft values the user has not edited follow it too, so the next Save
   * cannot revert someone else's change with stale values.
   */
  setSavedState(appSettings: Partial<AppConfig>) {
    const previous = this.#saved();
    const next = { ...previous, ...appSettings };
    this.#saved.set(next);
    this.store.update((draft) => rebaseDraft(draft, previous, next, this.#pendingFiles()));
  }

  /** Discards the draft and any pending files; the store returns to `saved`. */
  revert() {
    const saved = this.#saved();
    this.store.update(() => saved);
    this.#pendingFiles.set(new Map());
  }

  /**
   * Stages an image for upload on the next save and previews it right away by
   * pointing the asset at its `data:` URL.
   */
  setPendingAsset(key: string, file: File, dataUrl: string) {
    this.#pendingFiles.update((files) => new Map(files).set(key, file));
    this.updateAsset(key, { url: dataUrl });
  }

  /**
   * Removes the image shown for `key`: a pending file is dropped and the saved
   * image comes back; a saved custom image is replaced by the built-in default
   * (persisted on the next save).
   */
  resetAsset(key: string) {
    const fallback = this.#pendingFiles().has(key)
      ? this.#saved().assets[key]
      : configDefault.assets[key];
    this.#pendingFiles.update((files) => {
      if (!files.has(key)) return files;
      const next = new Map(files);
      next.delete(key);
      return next;
    });
    if (fallback) {
      this.updateAsset(key, { url: fallback.url });
    }
  }
}

/** Moves every draft value that still equals the old baseline to the new one. */
function rebaseDraft(
  draft: AppConfig,
  previous: AppConfig,
  next: AppConfig,
  pendingFiles: ReadonlyMap<string, File>
): AppConfig {
  const follow = <T>(mine: T, base: T | undefined, theirs: T) => (mine === base ? theirs : mine);

  const assets = { ...draft.assets };
  for (const [key, asset] of Object.entries(next.assets)) {
    if (!pendingFiles.has(key) && draft.assets[key]?.url === previous.assets[key]?.url) {
      assets[key] = asset;
    }
  }

  const styles = draft.styles.map((mine) => {
    const theirs = next.styles.find((style) => style.id === mine.id);
    if (!theirs) return mine;
    const base = previous.styles.find((style) => style.id === mine.id);
    return {
      ...mine,
      dark: follow(mine.dark, base?.dark, theirs.dark),
      light: follow(mine.light, base?.light, theirs.light),
    };
  });
  for (const style of next.styles) {
    if (!styles.some((s) => s.id === style.id)) styles.push(style);
  }

  return {
    ...draft,
    title: follow(draft.title, previous.title, next.title),
    gateway: follow(draft.gateway, previous.gateway, next.gateway),
    colorMode: follow(draft.colorMode, previous.colorMode, next.colorMode),
    developerMode: follow(draft.developerMode, previous.developerMode, next.developerMode),
    // Server-owned (admin only): never part of a draft, always the latest value.
    fileSharing: next.fileSharing,
    assets,
    styles,
  };
}

function isAppConfigEqual(a: AppConfig, b: AppConfig): boolean {
  if (a === b) return true;
  if (
    a.title !== b.title ||
    a.gateway !== b.gateway ||
    a.colorMode !== b.colorMode ||
    a.developerMode !== b.developerMode
  ) {
    return false;
  }

  const assetKeys = Object.keys(a.assets);
  if (assetKeys.length !== Object.keys(b.assets).length) return false;
  for (const key of assetKeys) {
    const left = a.assets[key];
    const right = b.assets[key];
    if (!right || left.url !== right.url || left.name !== right.name) return false;
  }

  if (a.styles.length !== b.styles.length) return false;
  return a.styles.every((style, i) => {
    const other = b.styles[i];
    return style.id === other.id && style.dark === other.dark && style.light === other.light;
  });
}
